import { actions } from '@/store'
import request from '@/requests'
import notifications from '@/util/Notifications'
import { mapWithConcurrency } from '@/util'
import { BaseScreenManager, MultiSelectList, ScrollSyncHandler } from '@/logic/common'

import { hasPermission } from '@/logic/common/Permissions'

import {
  STATUS_OK,
  STATUS_WARNING,
  TASK_TYPE_DESIGN,
  TASK_TYPE_SALES,
  TASK_TYPE_INSTALLATION,
  TASK_TYPE_CLIENT,
  TASK_TYPE_INSPECTION,
  STATUS_ERROR,
  MAX_CONCURRENT_BULK_ACTIONS,
  BULK_REFRESH_CHUNK_SIZE,
} from '@/constants'

import type {
  Address,
  ExistingGroup,
  Action,
  ActionData,
  TaskTypeKey,
  CustomResponse,
  // StoredAction,
} from '@/interfaces'

import type VueRouter from 'vue-router'
import MasterItem from './MasterItem'
import MasterItemList from './MasterItemList'
import TasksPaneManager from './TasksPaneManager'

interface StoredAction {
  item: MasterItem
  callback: Function
  action: Action
  taskType: TaskTypeKey
}

interface Project {
  name: string
  type: string
  year: number
  // present in the config service response; used to scope requests by project
  id?: number
}

interface TextValue {
  [value: string]: number
}

type TaskDataField = string | number | { [field: string]: string | number }

interface TaskDataCategoryTotals {
  total: number
  fields: {
    [field: string]: number
  }
}

interface TaskDataTotals {
  [attribute: string]: number | TextValue | TaskDataCategoryTotals
}

export default class TasksScreenManager extends BaseScreenManager {
  public groups: Array<string> = []
  public wocos: Array<string> = []
  public projects: Array<Project> = []
  public selectedGroup: string | null = null
  public selectedProject: string | null = null
  public selectedWoco: string | null = null
  public selectedMasterItem: null | any = null

  public wocoRequiredOnly = false

  public list: MultiSelectList<MasterItem>
  public panes: TasksPaneManager
  public scroll = new ScrollSyncHandler()

  public isLoading = false
  public isLoadingDetail = false

  constructor(router: VueRouter) {
    super(router)
    this.list = MasterItemList.createList()
    this.panes = new TasksPaneManager({
      update: {
        [TASK_TYPE_DESIGN]: async () => this.getDataForTaskType(TASK_TYPE_DESIGN),
        [TASK_TYPE_INSTALLATION]: async () => this.getDataForTaskType(TASK_TYPE_INSTALLATION),
        [TASK_TYPE_SALES]: async () => this.getDataForTaskType(TASK_TYPE_SALES),
        [TASK_TYPE_INSPECTION]: async () => this.getDataForTaskType(TASK_TYPE_INSPECTION),
        [TASK_TYPE_CLIENT]: this.getClients,
      },
      updateQueryParams: this.updateURLQueryParams,
      params: this.router.currentRoute.query || {},
    })
    this.init()
  }

  get hasAddresses() {
    return this.list.items.some((masterItem: MasterItem) => masterItem.address !== undefined)
  }

  protected init = async () => {
    const wocos = await this.getWocos()
    const q = this.router.currentRoute.query
    const selectedWoco = (q.woco as string) || actions.common.readSelectedWoco()
    if (typeof selectedWoco === 'string' && wocos.includes(selectedWoco)) {
      this.selectedWoco = selectedWoco
    }
    this.wocos = wocos

    const projects = await this.getProjects()
    const selectedProject = (q.project as string) || actions.common.readSelectedProject()
    if (
      typeof selectedProject === 'string' &&
      projects.find((project: Project) => project.name === selectedProject)
    ) {
      this.selectedProject = selectedProject
    }
    this.projects = projects

    const groups = await this.getGroups()
    const selectedGroup = (q.group as string) || actions.common.readSelectedGroup()
    if (typeof selectedGroup === 'string' && groups.includes(selectedGroup)) {
      this.setSelectedGroup(selectedGroup, true)
    }
    this.groups = groups

    const search = q.search || ''
    this.list.filter!.handler('search')(search)
  }

  public updateURLQueryParams = (keepAddressDetail = false) => {
    const { address, detail } = this.router.currentRoute.query || {}
    const query = {} as { [prop: string]: string }

    const openTaskTypes = this.panes.getTaskTypesOpenPanes()
    const detailTaskType = this.selectedMasterItem ? this.selectedMasterItem.detailTaskType : null
    const filterState = this.list.filter!.state

    if (filterState.searchValue) query.search = filterState.searchValue as string
    if (this.selectedWoco) query.woco = this.selectedWoco
    if (this.selectedProject) query.project = this.selectedProject
    if (this.selectedGroup) query.group = this.selectedGroup
    if (this.panes.openPanes.length > 0) query.panes = this.panes.openPanes.join(',')
    if (this.selectedMasterItem && !openTaskTypes.includes(detailTaskType)) {
      this.selectedMasterItem = null
    } else if (this.selectedMasterItem) {
      query.address = this.selectedMasterItem.address
      query.detail = this.selectedMasterItem.detailTaskType
    } else if (
      keepAddressDetail &&
      address &&
      detail &&
      openTaskTypes.includes(detail as TaskTypeKey)
    ) {
      query.address = address as string
      query.detail = detail as string
    }

    const queryString = new URLSearchParams(query).toString()
    const url = window.location.href.split('?')[0]
    window.location.replace(`${url}?${queryString}`)
  }

  public getActionsForSelectedItems = (taskType: TaskTypeKey) => {
    const items = this.list.selectedItems
    const taskActions: { [prop: string]: Array<StoredAction> } = items.reduce(
      (map, item) => {
        const task = item[taskType]
        if (task.actions) {
          task.actions.forEach((action: Action) => {
            if (action.permissions && !hasPermission(action.permissions)) return

            if (map[action.component] === undefined) {
              // eslint-disable-next-line no-param-reassign
              map[action.component] = []
            }
            const actionList = map[action.component]!
            actionList.push({
              item,
              callback: (options?: { skipRefresh?: boolean }) =>
                this.submitMasterItemAction(item, action, options),
              action,
              taskType,
            })
          })
        }

        return map
      },
      {} as { [prop: string]: StoredAction[] }
    )

    const filteredActions = Object.entries(taskActions).reduce(
      (acc, [actionComponent, actionList]) => {
        if (actionList.length === items.length) {
          return { ...acc, [actionComponent]: actionList }
        }
        return acc
      },
      {} as { [actionComponent: string]: Array<StoredAction> }
    )

    return filteredActions
  }

  public setActionDataForItems = (actionData: ActionData, list: Array<StoredAction>) => {
    list.forEach((entry) =>
      entry.item.setActionData({
        ...entry.item.actionData,
        ...actionData,
      })
    )
  }

  public submitActionForItems = async (actionList: Array<StoredAction>) => {
    const { action, taskType } = actionList[0]

    // assign_installer stays strictly sequential: it was made so deliberately and
    // its numbers are the reason for the cap below (0,4% of assigns exceed 10s
    // against 56,9% of approves, same backend, same database).
    const isSequentialAction =
      action.action === 'assign_installer' && ['installation', 'inspection'].includes(taskType)

    // submitMasterItemAction flips the row to loading itself, but it only does so
    // once a worker picks the row up. Mark the whole selection up front so the
    // screen still shows every selected row as busy from the first click, the way
    // it did when all callbacks fired in the same tick. The finally clears rows
    // that never got their turn, so a failure halfway cannot leave them spinning.
    actionList.forEach((entry) => entry.item.setIsLoading(true))

    // Rows are refreshed per chunk through the group endpoint instead of one GET
    // per row per open pane after every action (EH-1877). A row stays loading
    // until its chunk is refreshed, so it never shows the pre-action buttons
    // after its action went through.
    const responses: Array<CustomResponse | null> = []
    try {
      for (let i = 0; i < actionList.length; i += BULK_REFRESH_CHUNK_SIZE) {
        const chunk = actionList.slice(i, i + BULK_REFRESH_CHUNK_SIZE)
        // eslint-disable-next-line no-await-in-loop
        const chunkResponses = await mapWithConcurrency<StoredAction, CustomResponse | null>(
          chunk,
          isSequentialAction ? 1 : MAX_CONCURRENT_BULK_ACTIONS,
          (entry) => entry.callback({ skipRefresh: true })
        )
        responses.push(...chunkResponses)
        // Failed rows too: a 409 usually means the task already moved on
        // eslint-disable-next-line no-await-in-loop
        await this.updateListForAddresses(chunk.map((entry) => entry.item.address))
      }
    } finally {
      actionList.forEach((entry) => entry.item.setIsLoading(false))
    }

    const [successCount, failedCount] = responses.reduce(
      (acc, response) => {
        if (response?.status === STATUS_OK) {
          acc[0] += 1
        } else {
          acc[1] += 1
        }
        return acc
      },
      [0, 0]
    )

    actions.common.commitNotifications([])
    if (successCount > 0) {
      notifications.addNotification({
        message: `${successCount} successful ${successCount > 1 ? 'updates' : 'update'}`,
        type: 'success',
      })
    }
    if (failedCount > 0) {
      notifications.addNotification({
        message: `${failedCount} failed ${failedCount > 1 ? 'updates' : 'update'}`,
        type: 'danger',
      })
    }
  }

  public setSelectedGroup = async (newGroup: string, isInitialSelection = false) => {
    actions.common.commitSelectedGroup(newGroup)
    this.selectedGroup = newGroup
    this.selectedMasterItem = null

    if (!isInitialSelection) {
      actions.common.commitPanes([])
      this.panes.syncPaneSelectionWithStore(true)
    }

    if (newGroup) {
      await this.getAddresses()
      this.isLoading = false
    }

    this.updateURLQueryParams(isInitialSelection)
  }

  public getDataForTaskType = async (taskType: TaskTypeKey) => {
    if ((!this.selectedGroup && !this.wocoRequiredOnly) || !this.list.items.length) return

    const groups = this.wocoRequiredOnly
      ? this.getGroupsFromMasterItems()
      : [{ group: this.selectedGroup!, addresses: [] }]

    const responses = await Promise.all(
      groups.map(({ group, addresses }) => request.tasks.getAllTasksForGroup(group, taskType))
    )

    const taskData = responses.reduce((acc, response) => {
      if (response.status !== STATUS_OK) {
        notifications.addNotificationFromResponse(response)
      }
      return { ...acc, ...response.data }
    }, {})

    this.setTaskData(taskData, taskType)
    // const response = await request.tasks.getAllTasksForGroup(this.selectedGroup, taskType);

    // if ([STATUS_OK, STATUS_WARNING].includes(response.status)) {
    //   this.setTaskData(response.data, taskType);
    // }

    // if ([STATUS_WARNING, STATUS_ERROR].includes(response.status)) {
    //   notifications.addNotificationFromResponse(response);
    // }
  }

  public getClients = async () => {
    if (!this.selectedGroup || !this.list.items.length) return

    const response = await request.clients.getClientsForGroup(this.selectedGroup)
    if (response.status === STATUS_OK) {
      this.setTaskData(response.data, TASK_TYPE_CLIENT)
    } else {
      notifications.addNotificationFromResponse(response)
    }
  }

  public generateTaskDataStatistics = (
    taskType: TaskTypeKey,
    fields: Array<string> = [],
    useExclusionForFields = false
  ) => {
    const data = this.list.filteredItems.map((item) => item.getTaskData(taskType))
    const totals = data.reduce((stats, itemData) => {
      const acc = { ...stats }
      Object.entries(itemData).forEach(([attr, value]) => {
        if (
          fields.length > 0 &&
          (useExclusionForFields ? fields.includes(attr) : !fields.includes(attr))
        )
          return

        const v = value as TaskDataField
        const isNumericValue = !Number.isNaN(+v)

        if (acc[attr] === undefined) {
          if (typeof value !== 'object') {
            acc[attr] = isNumericValue ? 0 : {}
          } else {
            acc[attr] = { total: 0, fields: {} }
          }
        }

        if (isNumericValue) {
          ;(acc[attr] as number) += +v
        } else if (typeof v === 'string') {
          if ((acc[attr] as TextValue)[v] === undefined) {
            ;(acc[attr] as TextValue)[v] = 0
          }
          ;(acc[attr] as TextValue)[v] += 1
        } else {
          const fieldGroup = acc[attr] as TaskDataCategoryTotals
          Object.entries(v as { [field: string]: string }).forEach(([field, val]) => {
            fieldGroup.total += +val
            if (fieldGroup.fields[field] === undefined) {
              fieldGroup.fields[field] = 0
            }
            fieldGroup.fields[field] += +val
          })
        }
      })

      return acc
    }, {} as TaskDataTotals)

    return totals
  }

  public setSelectedProject = async (newProject: string) => {
    actions.common.commitSelectedProject(newProject)
    this.selectedProject = newProject
    this.setSelectedGroup('')

    if (newProject.trim()) {
      this.groups = await this.getGroups()
    } else {
      this.groups = []
    }
  }

  public setSelectedWoco = async (newWoco: string) => {
    this.wocoRequiredOnly = false
    actions.common.commitSelectedWoco(newWoco)
    this.selectedWoco = newWoco

    this.setSelectedProject('')

    if (newWoco.trim()) {
      this.projects = await this.getProjects()
    } else {
      this.projects = []
    }
  }

  public setSelectedMasterItem = async (
    masterItem: MasterItem,
    taskType: string,
    forceUpdate = false
  ) => {
    if (
      this.selectedMasterItem &&
      masterItem.address === this.selectedMasterItem.address &&
      this.selectedMasterItem.detailTaskType === taskType &&
      !forceUpdate
    )
      return

    if (!masterItem.getStatus(taskType as TaskTypeKey)) return

    if (this.selectedMasterItem && this.selectedMasterItem.address !== masterItem.address) {
      this.selectedMasterItem.setDetails(undefined)
    }

    this.isLoadingDetail = true

    const detailProject = this.selectedProject
    const response = await request.tasks.getTaskDetail(taskType, masterItem.address, detailProject)
    if (response.status === STATUS_OK) {
      const { components } = response.data
      this.selectedMasterItem = {
        ...masterItem,
        components,
        detailTaskType: taskType,
      }
      masterItem.setDetails(components)
      this.updateURLQueryParams()
    }
    this.isLoadingDetail = false
  }

  public hideDetails = async () => {
    this.selectedMasterItem = null
  }

  public updateList = async (address: string, taskType?: string) => {
    const masterItem = this.getMasterItemByAddress(address)
    if (!masterItem) {
      console.warn(`Failed to find masterItem for address [${address}]`)
      return
    }

    masterItem.setIsLoading(true)

    await this.updateTaskDataForItem(masterItem)
    masterItem.setIsLoading(false)
  }

  /**
   * Refresh many rows at once, e.g. after a bulk edit. updateList per address
   * costs one GET per address per open pane: 315 addresses gave ~1.500 GETs on a
   * single erp worker (EH-1877). With a selected group this asks the group
   * endpoint for the addresses in chunks instead, one request per chunk per pane.
   */
  public updateListForAddresses = async (addresses: Array<string>) => {
    const masterItems = addresses
      .map((address) => this.getMasterItemByAddress(address))
      .filter((item): item is MasterItem => item !== undefined)
    if (!masterItems.length) return

    if (!this.selectedGroup) {
      await mapWithConcurrency(masterItems, MAX_CONCURRENT_BULK_ACTIONS, (item) =>
        this.updateList(item.address)
      )
      return
    }

    const group = this.selectedGroup
    const chunks: Array<Array<string>> = []
    for (let i = 0; i < masterItems.length; i += BULK_REFRESH_CHUNK_SIZE) {
      chunks.push(masterItems.slice(i, i + BULK_REFRESH_CHUNK_SIZE).map((item) => item.address))
    }
    const jobs = this.panes
      .getTaskTypesOpenPanes()
      .flatMap((taskType) => chunks.map((chunk) => ({ taskType, chunk })))

    masterItems.forEach((item) => item.setIsLoading(true))
    try {
      await mapWithConcurrency(jobs, MAX_CONCURRENT_BULK_ACTIONS, async ({ taskType, chunk }) => {
        const response = await request.tasks.getAllTasksForGroup(group, taskType, chunk)
        if ([STATUS_OK, STATUS_WARNING].includes(response.status)) {
          this.setTaskData(response.data, taskType)
        } else {
          notifications.addNotificationFromResponse(response)
        }
      })
    } finally {
      masterItems.forEach((item) => item.setIsLoading(false))
    }
  }

  // skipRefresh: the caller refreshes the row itself, see submitActionForItems
  public submitMasterItemAction = async (
    masterItem: MasterItem,
    taskAction: Action,
    { skipRefresh = false }: { skipRefresh?: boolean } = {}
  ) => {
    const { url, action } = taskAction
    const taskTypeURL = masterItem.getTaskTypeFromURL(url!)
    if (!taskTypeURL) {
      console.warn('Failed to extract task type from URL:', url)
      return null
    }

    masterItem.setIsLoading(true)

    const response = await request.tasks.submitAction(url!, masterItem.actionData)

    if ([STATUS_OK, STATUS_WARNING].includes(response.status)) {
      masterItem.resetActionData()
      if (!skipRefresh) {
        await this.updateTaskDataForItem(masterItem)
      }

      if (this.selectedMasterItem && this.selectedMasterItem.address === masterItem.address) {
        this.setSelectedMasterItem(masterItem, this.selectedMasterItem.detailTaskType, true)
      }

      if (response.status === STATUS_WARNING) {
        notifications.addNotificationFromResponse(response)
      }
    } else {
      notifications.addNotificationFromResponse(response)
    }

    if (!skipRefresh) {
      masterItem.setIsLoading(false)
    }
    return response
  }

  public getMasterItemByAddress = (address: string) => {
    const masterItem = this.list.items.find((item) => item.address === address)
    return masterItem
  }

  public updateTaskDataForItem = async (masterItem: MasterItem) => {
    const taskTypes = this.panes.getTaskTypesOpenPanes()
    const requests = taskTypes.map(
      (taskType) =>
        new Promise((resolve) => {
          // Scope to the board's project: the unscoped endpoint returns the
          // default project's task on multi-project addresses
          request.tasks.getTaskData(taskType, masterItem.address, this.selectedProject).then((taskUpdateResponse) => {
            resolve({
              response: taskUpdateResponse,
              taskType,
            })
          })
        })
    )

    const results = (await Promise.all(requests)) as Array<{
      response: CustomResponse
      taskType: string
    }>

    results.forEach((result) => {
      if ([STATUS_OK, STATUS_WARNING].includes(result.response.status)) {
        const taskData = result.response.data[masterItem.address]
        masterItem.setTypeData(result.taskType, taskData)
      }
    })
  }

  // The config id of the selected project; scopes per-row requests on
  // multi-project addresses
  get selectedProjectId(): number | null {
    const project = this.projects.find((p) => p.name === this.selectedProject)
    return project && project.id != null ? project.id : null
  }

  protected createNewMasterItem = (address: Address) => {
    const item = new MasterItem(address)
    item.projectId = this.selectedProjectId
    return item
  }

  protected getGroupsFromMasterItems = () => {
    // Pair each project with a group that actually belongs to it: groups[0]
    // can be a group of another project on a multi-project address
    const groupForItem = (item: MasterItem) =>
      item.groups.find((group) => item.groupProjects[group] === item.project) ?? item.groups[0]

    const projects = this.list.getItems().reduce(
      (acc, item) => {
        if (acc[item.project] === undefined) {
          return {
            ...acc,
            [item.project]: {
              addresses: [item.address],
              group: groupForItem(item),
            },
          }
        }
        acc[item.project].addresses.push(item.address)
        return acc
      },
      {} as { [project: string]: { addresses: Array<string>; group: string } }
    )

    return Object.values(projects)
  }

  protected getGroups = async () => {
    if (!this.selectedWoco || !this.selectedProject) return []

    const response = await request.groups.getAllGroupsForProject(
      this.selectedWoco,
      this.selectedProject,
      actions.auth.readUser()
    )

    // replace with real value once roles are implemented
    const isAdmin = false

    if (response.status === STATUS_OK) {
      return response.data
        .filter(
          (group: ExistingGroup) =>
            !group.is_private || group.created_by === actions.auth.readUser() || isAdmin
        )
        .map((group: ExistingGroup) => group.name)
    }
    notifications.addNotificationFromResponse(response)
    return []
  }

  protected getWocos = async () => {
    const response = await request.groups.getAllWocos()

    if (response.status === STATUS_OK) {
      return response.data
    }
    notifications.addNotificationFromResponse(response)
    return []
  }

  protected getProjects = async () => {
    if (!this.selectedWoco) return []
    const response = await request.groups.getAllProjectsForWoco(this.selectedWoco)

    if (response.status === STATUS_OK) {
      return response.data
    }
    notifications.addNotificationFromResponse(response)
    return []
  }

  protected getAddresses = async () => {
    if (!this.selectedGroup) return

    const initialFilterState = MasterItemList.createFilterState()
    this.list.filter!.setState({ ...initialFilterState })

    this.isLoading = true
    const response = await request.groups.getGroupVisibleForUser(
      this.selectedGroup,
      actions.auth.readUser()
    )

    if (response.status === STATUS_OK) {
      this.list.setItems(
        response.data.addresses.map((address: Address) => this.createNewMasterItem(address))
      )
    } else {
      this.list.setItems([])
      notifications.addNotificationFromResponse(response)
    }

    this.isLoading = false
  }

  protected setTaskData = (tasks: { [address: string]: object }, taskType: string) => {
    const masterItems = this.list.items
    const params = this.router.currentRoute.query || {}

    masterItems.forEach((masterItem) => {
      const taskData = tasks[masterItem.address]
      if (taskData === undefined) {
        return
      }
      masterItem.setTypeData(taskType, taskData)
      if (!this.selectedMasterItem && masterItem.address === params.address) {
        this.setSelectedMasterItem(masterItem, params.detail as string)
      }
    })

    this.list.setItems(masterItems)
  }
}
